import React, { CSSProperties, Fragment, memo } from 'react';
import cn from 'classnames';
import styles from './index.module.scss';
import { ICTAs } from '../../store';
import { useSpring, animated } from 'react-spring';
import {
  mediaAnimationProps,
  mapCTAsAlignment,
  mapButtonSizeStyle,
} from '../../common';
import {
  colorPrimary,
  colorWhite,
  toCamelCase,
  toCssColor,
} from '~/common/utils';
import {
  Button,
  ButtonProps,
  ButtonTypeEnum,
} from '~/common/components/ui-elements/button';
import { Modal } from '~/common/components/ui-elements/modal';
import { IconEnum, SvgIcon } from '~/common/components/ui-elements/svg-icon';
import { Property } from 'csstype';
import { usePortals } from 'react-portal-hook';

const HeroCardCtas = memo(
  ({ contentCtas, visible, alignment, isStackable, parentId }: ICTAs) => {
    const animateStyle = useSpring(mediaAnimationProps(visible));
    const portalManager = usePortals();
    const _contentCTAs = contentCtas?.filter((x) => !x.ctaHidden);

    const handleModalView = (url: string) => {
      portalManager.open((portal) => (
        <YTModal closeModal={portal.close} url={url} />
      ));
    };

    return (
      <div
        className={cn(styles.HeroCardCtas, {
          [styles.isNotStackable]: !isStackable,
        })}
      >
        {_contentCTAs && _contentCTAs.length > 0 && (
          <animated.div
            style={{
              ...animateStyle,
              ...(isStackable
                ? {
                    display: 'flex',
                    flexDirection: 'column',
                    ...mapCTAsAlignment(alignment || ''),
                  }
                : alignment
                ? {
                    textAlign: alignment as Property.TextAlign,
                  }
                : {}),
            }}
          >
            {_contentCTAs?.map((cta, idx) => {
              const _style: CSSProperties = {
                ...(cta.ctaColor ? { color: toCssColor(cta.ctaColor) } : {}),
                ...(cta.ctaColor
                  ? { borderColor: toCssColor(cta.ctaColor) }
                  : {}),
                ...mapButtonSizeStyle(cta.ctaButtonSize || ''),
              };
              const _target = cta.ctaUrl?.target || '_self';
              const buttonProps: ButtonProps = {
                type: toCamelCase(
                  cta.ctaButtonType
                ) as keyof typeof ButtonTypeEnum,
                style: _style,
                buttonWidth: cta.ctaFullWidth,
                buttonSize:
                  (cta.ctaButtonSize?.toLowerCase() as ButtonProps['buttonSize']) ||
                  'medium',
              };

              if (cta.contentType === 'CallToAction') {
                buttonProps.href = cta.ctaUrl?.url;
                buttonProps.target = _target;

                if (
                  cta.ctaUrl?.url &&
                  cta.ctaUrl?.url.includes(window.location.pathname) &&
                  _target === '_self'
                ) {
                  buttonProps.onClick = () => {
                    window.localStorage.setItem('prevSectionId', `${parentId}`);
                  };
                }
              } else if (cta.contentType === 'CtaToModal') {
                buttonProps.onClick = () => {
                  if (cta.ctaUrl?.url) handleModalView(cta.ctaUrl?.url);
                  else console.error('YT url is missing');
                };
              }

              const renderCTA = () => (
                <Fragment key={idx}>
                  <Button
                    className={cn(
                      styles.HeroCardCta,
                      styles[`HeroCardCta${buttonProps.type}`],
                      {
                        [styles.HeroCardCtaColor]: !!cta.ctaColor,
                      }
                    )}
                    {...buttonProps}
                  >
                    {cta.ctaIcon && cta.ctaIcon !== 'none' && (
                      <SvgIcon
                        type={cta.ctaIcon as keyof typeof IconEnum}
                        color={`#${colorPrimary}`}
                        size={1.2}
                        strokeWidth={2}
                        style={{
                          padding: '0px 5px 1.5px 0px',
                          alignSelf: 'start',
                          lineHeight: 'unset',
                        }}
                      />
                    )}
                    <span
                      className={styles.HeroCardCtaText}
                      style={
                        buttonProps.type === 'text'
                          ? { color: `#${colorPrimary}` }
                          : {}
                      }
                    >
                      {cta.ctaUrl?.name || ''}
                    </span>
                  </Button>
                </Fragment>
              );

              return (
                <Fragment key={idx}>
                  {isStackable ? (
                    <div
                      className={styles.HeroCardCtaContainer}
                      style={{
                        ...(cta.ctaButtonAlignment || alignment
                          ? {
                              textAlign:
                                cta.ctaButtonAlignment ||
                                (alignment as Property.TextAlign),
                            }
                          : {}),
                      }}
                    >
                      {renderCTA()}
                    </div>
                  ) : (
                    renderCTA()
                  )}
                </Fragment>
              );
            })}
          </animated.div>
        )}
      </div>
    );
  }
);

interface ModalProps {
  closeModal: () => void;
  url: string;
}

const YTModal: React.FC<ModalProps> = ({ closeModal, url }) => {
  const urlParts = new URL(url);
  const isEmbedLink = urlParts.pathname.includes('embed');
  const videoId = urlParts.searchParams.get('v');
  const embedUrl = isEmbedLink
    ? url
    : videoId
    ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`
    : null;

  const renderYT = () => {
    return (
      <>
        {embedUrl ? (
          <iframe
            src={embedUrl}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div>Error loading YouTube video</div>
        )}
      </>
    );
  };

  return (
    <Modal
      closeModal={closeModal}
      modalContentClass={cn(styles.YTModalContent)}
      closeButtonColor={`#${colorWhite}`}
      lockScroll
    >
      <div className={cn(styles.YTMediaContainerModal)}>{renderYT()}</div>
    </Modal>
  );
};

export default HeroCardCtas;
